import {
  Button,
  ButtonGroup,
  Grid,
  Heading,
  Input,
  Label,
  SelectField,
  Text,
  View,
} from "@aws-amplify/ui-react"
import type {
  OrganizationDto,
  UserDataDto,
  UserOrgsRolesDto,
} from "@strtmn/sds/types"
import { userOrgsRolesList } from "@strtmn/sds/users"
import { getErrorMessage } from "@strtmn/strtmn"
import { I18n } from "aws-amplify/utils"
import { useState } from "react"
import {
  Controller,
  FormProvider,
  useForm,
  type SubmitHandler,
} from "react-hook-form"
import { useNavigate } from "react-router-dom"
import { addUserToOrg, createUser, updateUser } from "../../api/userData"
import { isProd } from "../../config"
import { CancelButton, CopyToClipboard } from ".."

interface AddToOrgFormProps {
  allOrgs: OrganizationDto[]
  userData: UserDataDto
  setShowAddToOrgForm: React.Dispatch<React.SetStateAction<boolean>>
}
const AddToOrgForm = ({
  allOrgs,
  userData,
  setShowAddToOrgForm,
}: AddToOrgFormProps) => {
  const navigate = useNavigate()
  const methods = useForm<{ orgId: string; orgRole: UserOrgsRolesDto }>({
    defaultValues: { orgId: "", orgRole: "CUSTOMER" },
  })
  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    setError,
  } = methods

  const onAddToOrg = async (data: {
    orgId: string
    orgRole: UserOrgsRolesDto
  }) => {
    console.log("▶️ onAddToOrg")
    console.log("🪅🪅🪅 data:", data)
    const selectedOrg = allOrgs.find((o) => o.id === data.orgId)
    if (!selectedOrg) {
      console.warn(`Could not find org ${data.orgId}`)
      setError("root.clientError", {
        message: `${I18n.get("Fehler")}: ${I18n.get("Betrieb nicht gefunden.")}`,
      })
      return
    }

    // Prevent duplicates
    if (userData.Organizations?.find((o) => o.id === selectedOrg.id)) {
      console.log(`User is already member of org ${data.orgId}`)
      setError("root.clientError", {
        message: `${I18n.get("Fehler")}: ${I18n.get("Die Maschine ist bereits Teil des Betriebs.")}`,
      })
      return
    }

    // const updated = {
    //   ...userData,
    //   Organizations: [...userData.Organizations, selectedOrg],
    // }

    // setMyObject(updated)

    // Optionally call your backend here
    console.log("Send to backend:", {
      user_id: userData.id,
      org_id: data.orgId,
      org_role: data.orgRole,
    })
    try {
      await addUserToOrg(userData.id, data.orgId, data.orgRole)
      reset()
      setShowAddToOrgForm(false)
      navigate(0)
    } catch (error) {
      const eAsAny = error as any
      setError("root.serverError", {
        type: eAsAny.statusCode,
        message: isProd
          ? "Fehler beim Speichern."
          : "Fehler beim Speichern: " + getErrorMessage(eAsAny.body),
      })
    }
  }

  for (const org of allOrgs) {
    console.log("org:", org)
  }
  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onAddToOrg)}>
        <Text color="white">{I18n.get("Zu einem Betrieb hinzufügen")}</Text>

        {errors?.root && errors?.root["clientError"] && (
          <p className="error">{errors.root["clientError"].message}</p>
        )}
        {errors?.root && errors?.root["serverError"] && (
          <p className="error">{errors.root["serverError"].message}</p>
        )}

        <Controller
          name="orgId"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <SelectField label="" labelHidden={true} {...field}>
              <option value={0}>
                {I18n.get("Wählen Sie einen Betrieb aus")}
              </option>
              {allOrgs.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name} |{" "}
                  {o.address
                    ? typeof o.address === "string"
                      ? o.address
                      : o.address.city + ", " + o.address.street
                    : I18n.get("ohne Adresse")}{" "}
                  {/* | {o.type} */}
                </option>
              ))}
            </SelectField>
          )}
        />
        <Label className="!mt-2" color="white">
          {I18n.get("Rolle")}
        </Label>
        <Controller
          name="orgRole"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <SelectField label="" labelHidden={true} {...field}>
              <option value={0}>{I18n.get("Wählen Sie eine Rolle aus")}</option>
              {userOrgsRolesList.map((r: string) => (
                <option key={r} value={r}>
                  {I18n.get(r)}
                </option>
              ))}
            </SelectField>
          )}
        />
        <Text color="white">
          {I18n.get(
            "Benutzer, die einem Betrieb zugeordnet sind, haben dann Zugriff auf die Maschinen in diesem Betrieb."
          )}
        </Text>
        <Text color="white">{I18n.get('Die Standard-Rolle ist "Kunde".')}</Text>
        <ButtonGroup className="buttons !mt-4" size="small">
          <Button variation="primary" type="submit">
            {I18n.get("Speichern")}
          </Button>
          <Button
            backgroundColor="white"
            onClick={() => {
              reset()
              setShowAddToOrgForm(false)
            }}
          >
            {I18n.get("Abbrechen")}
          </Button>
        </ButtonGroup>
      </form>
    </FormProvider>
  )
}

interface UserMaskProps {
  user: UserDataDto
  isNewUser: boolean
  allOrgs: OrganizationDto[]
}

export default function UserMask({ user, isNewUser, allOrgs }: UserMaskProps) {
  console.log("> UserMask")
  console.log("user:", user)
  console.log("isNewUser:", isNewUser)
  // console.log("allOrgs:", allOrgs)
  const navigate = useNavigate()
  const [isFormSubmitting /*, setIsFormSubmitting*/] = useState(false)
  const [showAddToOrgForm, setShowAddToOrgForm] = useState(false)

  if (isNewUser) {
    user.first_name = ""
    user.last_name = ""
  }

  const methods = useForm<UserDataDto>({
    values: user,
  })
  const {
    register,
    handleSubmit,
    setError,
    // setValue,
    formState: { errors },
    // watch,
  } = methods

  const onSubmit: SubmitHandler<UserDataDto> = async (data) => {
    console.log("▶️ onSubmit")
    console.log("data:", data)
    try {
      if (isNewUser) {
        await createUser(data)
      } else {
        await updateUser(data)
      }
      window.history.replaceState({}, "")
      navigate(0)
    } catch (err) {
      setError("root.serverError", {
        message: `${I18n.get("Fehler")}: ${I18n.get("Konnte den Benutzer nicht speichern.")}`,
      })
    }
  }

  return (
    <Grid rowGap="0.5rem" columnGap="0.5rem">
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          {errors?.root && errors?.root["clientError"] && (
            <p className="error">{errors.root["clientError"].message}</p>
          )}
          {errors?.root && errors?.root["serverError"] && (
            <p className="error">{errors.root["serverError"].message}</p>
          )}

          <Heading className="!border-solid !border-0 !border-b-4 !pb-2 !mb-2">
            {I18n.get("Stammdaten")}
          </Heading>
          {!isProd && (
            <>
              <Label>{I18n.get("User ID")}</Label>
              <div>
                {user.id} <CopyToClipboard text={user.id} />
              </div>
              <div>{/* divider */}&nbsp;</div>
            </>
          )}

          <Label>{I18n.get("Nachname")}</Label>
          <Input
            {...register("last_name")}
            placeholder={I18n.get("Nachname")}
          />
          <Label>{I18n.get("Vorname")}</Label>
          <Input
            {...register("first_name")}
            placeholder={I18n.get("Vorname")}
          />
          <Label>{I18n.get("E-Mail")}</Label>
          <Input {...register("email")} placeholder="E-Mail" />

          <ButtonGroup className="buttons !mt-4" size="small">
            <Button
              type="submit"
              variation="primary"
              isDisabled={isFormSubmitting}
              isLoading={isFormSubmitting}
              loadingText={I18n.get("Wird gespeichert...")}
            >
              {I18n.get("Speichern")}
            </Button>
            <CancelButton
              href=".."
              title="Abbrechen"
              isDisabled={isFormSubmitting}
            />
          </ButtonGroup>
        </form>
      </FormProvider>

      {!isNewUser && (
        <>
          <div>{/* divider */}&nbsp;</div>

          <Heading className="!border-solid !border-0 !border-b-4 !pb-2">
            {I18n.get("Betriebe")}
          </Heading>
          {!user.Organizations ||
          !Array.isArray(user.Organizations) ||
          user.Organizations.length === 0 ? (
            <div>
              {I18n.get("Dieser Benutzer ist keinem Betrieb zugewiesen.")}
            </div>
          ) : (
            <table className="user-orgs">
              <thead>
                <tr>
                  <th>{I18n.get("Betrieb")}</th>
                  {/* <th>{I18n.get("Betriebstyp")}</th> */}
                  <th>{I18n.get("Rolle")}</th>
                  {!isProd && <th>Betriebs-ID</th>}
                </tr>
              </thead>
              <tbody>
                {user.Organizations.sort((a, b) =>
                  a.name.localeCompare(b.name, undefined, {
                    sensitivity: "base",
                    numeric: true,
                  })
                ).map((org) => (
                  <tr key={org.id}>
                    <td>{org.name}</td>
                    {/* <td>{I18n.get(org.type ?? "unknown")}</td> */}
                    <td>
                      {org.user_role ?? I18n.get("Keine Rolle festgelegt")}
                    </td>
                    {!isProd && (
                      <td>
                        {org.id} <CopyToClipboard text={org.id} />
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {showAddToOrgForm ? (
            <View className="bg-slate-400 p-4">
              <AddToOrgForm
                allOrgs={allOrgs}
                userData={user}
                setShowAddToOrgForm={setShowAddToOrgForm}
              />
            </View>
          ) : (
            <View>
              <Button size="small" onClick={() => setShowAddToOrgForm(true)}>
                {I18n.get("Zu einem Betrieb hinzufügen")}
              </Button>
            </View>
          )}

          {/* <table style={{ width: "50%" }}>
              <thead>
                <tr>
                  <th>{I18n.get("Rollen")}</th>
                </tr>
              </thead>
              <tbody>
                {user.roles.map((role) => (
                  <tr key={role.id}>
                    <td>{role.name}</td>
                  </tr>
                ))}
              </tbody>
            </table> */}
        </>
      )}
    </Grid>
  )
}
